import { useQuery } from "@apollo/client";
import { useUserData } from "graphql/user";
import { TASK_STATUS_TO_BACKEND } from "pages/Tasks/constants";
import { useEffect, useMemo } from "react";
import { FiltersExpression, gql, Operation, Operator, TaskStatus } from "types";

const POLL_INTERVAL_MS = 60000;

const USER_TASKS_COUNT = gql(/* GraphQL */ `
  query UserTasksCountPolling($filters: FiltersExpression) {
    tasksList(filters: $filters) {
      count
    }
  }
`);

const buildUserTasksFilter = (userId: string): FiltersExpression => ({
  operator: Operator.And,
  filters: [
    {
      field: "task.status",
      op: Operation.Neq,
      values: [TASK_STATUS_TO_BACKEND[TaskStatus.Closed]],
    },
    {
      field: "task.assignedToId",
      op: Operation.Eq,
      values: [userId],
    },
  ],
});

type Props = {
  enabled?: boolean;
};

export const useTasksCountPolling = ({ enabled = false }: Props) => {
  const { user } = useUserData();
  const userId = user?.id;

  const filters = useMemo(
    () => (userId ? buildUserTasksFilter(userId) : undefined),
    [userId],
  );

  const { data, refetch } = useQuery(USER_TASKS_COUNT, {
    variables: { filters },
    skip: !enabled || !userId,
    errorPolicy: "ignore",
  });

  useEffect(() => {
    if (!enabled || !userId) return;

    const interval = setInterval(() => {
      void refetch();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refetch, enabled, userId]);

  return { count: data?.tasksList.count ?? 0 };
};
